import {
  IndexNavAll,
  IndexNavBrands,
  IndexNavCountries,
  IndexNavMagazineNews,
  IndexNavNew,
  IndexNavSellNow,
} from '@/components/home/Index/IndexNav/IndexNavs';
import { Routes } from '@/types/routes.enum';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { HeaderEnum } from '@/types/translations/HeaderEnum';
import { IndexV2Enum } from '@/types/translations/IndexV2Enum';
import DefaultLink from '@/components/common/default/DefaultLink';
import { IndexNavProps } from '@/types';
import useNavItems from '../../../../hooks/useNavItems';
import { useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import useBackendUser from '@/hooks/useBackendUser';

export default function IndexNav(_props: IndexNavProps) {
  const { translate } = useTypedTranslation<IndexV2Enum>('index-v2');
  const { translate: translateHeader } = useTypedTranslation<HeaderEnum>('header');
  const { indexMegaMenuBrands, indexMegaMenuCountries } = useNavItems();
  const navRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { data: user } = useBackendUser();

  const updateCssVar = useCallback(() => {
    if (navRef.current) {
      const bottom = navRef.current.getBoundingClientRect().bottom;
      // Only set the variable if it's visible (bottom > 0) to avoid `display: none` overwriting with 0
      if (bottom > 0) {
        document.documentElement.style.setProperty('--index-nav-bottom', `${bottom}px`);
      }
    }
  }, []);

  useEffect(() => {
    // Trigger on mount
    updateCssVar();

    // Delays to catch layout shifts from fonts, images, or hydration
    const t1 = setTimeout(updateCssVar, 100);
    const t2 = setTimeout(updateCssVar, 500);
    const t3 = setTimeout(updateCssVar, 1000);

    // Watch for DOM layout shifts (like lazy loaded images or banners above the menu)
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        requestAnimationFrame(updateCssVar);
      });
      observer.observe(document.body);
    }

    // Trigger on client navigation
    router.events.on('routeChangeComplete', updateCssVar);

    // Trigger on resize & scroll
    const handleEvent = () => requestAnimationFrame(updateCssVar);
    window.addEventListener('scroll', handleEvent, { passive: true });
    window.addEventListener('resize', handleEvent, { passive: true });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      observer?.disconnect();
      router.events.off('routeChangeComplete', updateCssVar);
      window.removeEventListener('scroll', handleEvent);
      window.removeEventListener('resize', handleEvent);
    };
  }, [updateCssVar, router.events]);

  return (
    <div
      ref={navRef}
      className="tw-mb-8 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 tw-px-2"
    >
      {/* always visible */}
      <IndexNavAll />

      {/* sm+ */}
      <div className="tw-hidden sm:tw-block">
        <IndexNavBrands indexMegaMenuBrands={indexMegaMenuBrands} />
      </div>
      <div className="tw-hidden sm:tw-block">
        <IndexNavNew />
      </div>
      {/* lg+ */}
      <div className="tw-hidden sm:tw-block">
        <IndexNavCountries indexMegaMenuCountries={indexMegaMenuCountries} />
      </div>
      <div>
        <DefaultLink
          className="tw-mx-0 tw-my-0 tw-appearance-none tw-border-none tw-bg-transparent tw-px-0 tw-py-0 tw-text-base tw-font-medium tw-text-textBlack tw-shadow-none tw-outline-none focus:tw-outline-none focus:tw-ring-0 max-nine:tw-text-xs-custom nine:tw-max-w-xs"
          href={Routes._COLLECTIONS}
        >
          {translate(IndexV2Enum['home_nav.collections'])}
        </DefaultLink>
      </div>
      <div className="tw-hidden sm:tw-block">
        <IndexNavSellNow />
      </div>

      {/* md+ */}
      <div className="tw-hidden md:tw-block">
        <IndexNavMagazineNews />
      </div>
      <div className="tw-hidden md:tw-block">
        <DefaultLink
          className="tw-mx-0 tw-my-0 tw-appearance-none tw-border-none tw-bg-transparent tw-px-0 tw-py-0 tw-text-base tw-font-medium tw-text-textBlack tw-shadow-none tw-outline-none focus:tw-outline-none focus:tw-ring-0 max-nine:tw-text-xs-custom nine:tw-max-w-xs"
          href={user ? Routes._DASHBOARD : Routes._PORTFOLIO_GUIDE}
        >
          {translateHeader(HeaderEnum['your_portfolio'])}
        </DefaultLink>
      </div>
    </div>
  );
}
